import { collection, doc, getDocs, getDoc, query, where, orderBy, updateDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

const COLLECTION_NAME = "workoutPrescriptions";

/**
 * Busca TODAS as sessões de um membro, de todas as prescrições ativas.
 * Usada para a visão semanal do professor.
 */
export const getAllSessionsByMember = async (idTenant, idMember) => {
    if (!idTenant || !idMember) return [];

    try {
        const prescriptionsQuery = query(
            collection(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME),
            where("status", "==", "active")
        );
        const prescSnap = await getDocs(prescriptionsQuery);
        if (prescSnap.empty) return [];

        const allSessions = [];
        for (const prescDoc of prescSnap.docs) {
            const prescData = prescDoc.data();
            const sessionsSnap = await getDocs(
                collection(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescDoc.id, "workoutSessions")
            );
            sessionsSnap.docs.forEach(sDoc => {
                allSessions.push({
                    id: sDoc.id,
                    ...sDoc.data(),
                    idPrescription: prescDoc.id,
                    templateTitle: prescData.templateTitle || "",
                });
            });
        }

        allSessions.sort((a, b) => {
            if (a.scheduledDate && b.scheduledDate) return a.scheduledDate.localeCompare(b.scheduledDate);
            return (a.sessionNumber || 0) - (b.sessionNumber || 0);
        });

        return allSessions;
    } catch (error) {
        console.error("Erro ao buscar todas as sessões do membro:", error);
        return [];
    }
};

/**
 * Busca todas as sessões de uma prescrição específica.
 */
export const getSessionsByPrescription = async (idTenant, idMember, prescriptionId) => {
    if (!idTenant || !idMember || !prescriptionId) return [];

    try {
        const q = query(
            collection(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescriptionId, "workoutSessions"),
            orderBy("sessionNumber", "asc")
        );
        const snapshot = await getDocs(q);
        return snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
    } catch (error) {
        console.error("Erro ao buscar sessões da prescrição:", error);
        return [];
    }
};

/**
 * Busca a próxima sessão pendente de um treino específico (workoutIndex).
 * Usada pelo Totem para saber qual sessão consumir ao imprimir.
 */
export const getNextPendingSession = async (idTenant, idMember, prescriptionId, workoutIndex) => {
    if (!idTenant || !idMember || !prescriptionId) return null;

    try {
        const constraints = [where("status", "==", "pending")];

        if (workoutIndex !== undefined && workoutIndex !== null) {
            constraints.push(where("workoutIndex", "==", workoutIndex));
        }

        const q = query(
            collection(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescriptionId, "workoutSessions"),
            ...constraints
        );
        const snapshot = await getDocs(q);

        if (snapshot.empty) return null;

        const sessions = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        sessions.sort((a, b) => (a.sessionNumber || 0) - (b.sessionNumber || 0));

        return sessions[0];
    } catch (error) {
        console.error("Erro ao buscar próxima sessão pendente:", error);
        return null;
    }
};

/**
 * Marca uma sessão como completada e incrementa o contador na prescrição pai.
 * @param {string} completedVia - "kiosk_print" | "app" | "manual"
 */
export const completeSession = async (idTenant, idMember, prescriptionId, sessionId, completedVia = "kiosk_print") => {
    if (!idTenant || !idMember || !prescriptionId || !sessionId) {
        throw new Error("Todos os IDs são obrigatórios para completar sessão.");
    }

    try {
        const sessionRef = doc(
            db, "tenants", idTenant, "members", idMember,
            COLLECTION_NAME, prescriptionId, "workoutSessions", sessionId
        );

        const sessionSnap = await getDoc(sessionRef);
        const sessionData = sessionSnap.exists() ? sessionSnap.data() : {};

        await updateDoc(sessionRef, {
            status: "completed",
            completedAt: serverTimestamp(),
            completedVia,
            updatedAt: serverTimestamp(),
        });

        const prescriptionRef = doc(db, "tenants", idTenant, "members", idMember, COLLECTION_NAME, prescriptionId);
        const prescriptionSnap = await getDoc(prescriptionRef);

        if (prescriptionSnap.exists()) {
            const pData = prescriptionSnap.data();
            const newCount = (pData.completedSessionsCount || 0) + 1;
            const totalExpected = pData.totalExpectedSessions || 0;

            const updateFields = {
                completedSessionsCount: newCount,
                lastCompletedWorkoutId: sessionId,
                lastCompletedWorkoutName: sessionData.workoutName || null,
                lastCompletedAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
            };

            if (totalExpected > 0 && newCount >= totalExpected) {
                updateFields.status = "completed";
            }

            await updateDoc(prescriptionRef, updateFields);
        }

        return true;
    } catch (error) {
        console.error("Erro ao completar sessão:", error);
        throw error;
    }
};
